'use client'
import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import InlineLinkList from '@/components/common/InlineLinkList'
import { consumerRequest } from '@/helper/request'
import { PRODUCT_CITY_ALL_API } from '@/helper/apiConstants'
import {
  getFooterProductContext,
  buildTestsInOtherCitiesItems,
} from '@/utils/commonFunc'
import { CURRENT_SUBSIDIARY, DEFAULT_CITY_SLUG } from '@/utils/constants'
import type { FooterProductContext } from '@/utils/commonFunc'

interface TestsInOtherCitiesLinksProps {
  fetchedContext: FooterProductContext | null
  cityPrices: any[]
  serviceableCitySlugs: string[]
}

const contextKey = (context: FooterProductContext | null) =>
  context?.productId
    ? `${context.productId}:${context.citySlug || DEFAULT_CITY_SLUG}`
    : ''

// The root layout never re-renders on client-side navigation, so the server pass only ever has the
// product from the last full page load; this refetches when the route points at a different PDP.
const TestsInOtherCitiesLinks = ({
  fetchedContext,
  cityPrices,
  serviceableCitySlugs,
}: TestsInOtherCitiesLinksProps) => {
  const pathname = usePathname() || ''
  const context = getFooterProductContext(pathname)
  const [fetched, setFetched] = useState<Record<string, any[]>>(() =>
    fetchedContext ? { [contextKey(fetchedContext)]: cityPrices } : {}
  )
  // Keyed by product:city so a route already fetched never re-requests, however often this re-renders.
  const inFlightRef = useRef<Set<string>>(new Set())

  const productId = context?.productId
  const key = contextKey(context)
  const resolved = fetched[key]

  const citySlug = context?.citySlug || DEFAULT_CITY_SLUG
  // Empty list means "could not verify", so it must not suppress every city.
  const isServiceable =
    !serviceableCitySlugs?.length || serviceableCitySlugs.includes(citySlug)

  useEffect(() => {
    const payload = {
      productId: context?.productId || '',
      citySlug,
      subsidiary: CURRENT_SUBSIDIARY,
    }
    // Logged before the guard so every scenario is visible, skips included.
    const skipReason = !key
      ? 'not-a-pdp-route'
      : !isServiceable
      ? 'city-not-serviceable'
      : resolved
      ? 'already-cached'
      : inFlightRef.current.has(key)
      ? 'in-flight'
      : ''

    if (skipReason) return

    inFlightRef.current.add(key)
    consumerRequest(PRODUCT_CITY_ALL_API, 'post', payload)
      .then((apiResponse: any) => {
        if (!apiResponse?.status) {
          console.error(
            'Failed to refetch tests in other cities after navigation',
            apiResponse?.message
          )
        }
        const data =
          apiResponse?.status && Array.isArray(apiResponse?.data)
            ? apiResponse.data
            : []
        // Cached even on failure, so a failing product retries on next navigation rather than in a loop.
        setFetched((prev) => ({ ...prev, [key]: data }))
      })
      .finally(() => {
        inFlightRef.current.delete(key)
      })
  }, [key, resolved, isServiceable, context?.productId, citySlug, pathname])

  if (!productId) return null

  const built = buildTestsInOtherCitiesItems(
    resolved || [],
    productId,
    serviceableCitySlugs
  )
  if (!built) return null

  return (
    <InlineLinkList
      id="tests-in-other-cities"
      heading={built.heading}
      items={built.items}
    />
  )
}

export default TestsInOtherCitiesLinks
